import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildAveChainlitIframeUrl,
  consumeAveChainlitContext,
  createAveChainlitContextFromQuery,
  getAveChainlitUrl,
} from '../utils/aveChainlit';
import './AveChainlitChat.css';

const parseWindowMessage = (data) => {
  if (typeof data !== 'string') {
    return data;
  }

  try {
    return JSON.parse(data);
  } catch (_) {
    return null;
  }
};

const getOrigin = (url) => {
  try {
    return new URL(url, window.location.origin).origin;
  } catch (_) {
    return '*';
  }
};

function AveChainlitChat() {
  const iframeRef = useRef(null);
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [ready, setReady] = useState(false);

  const chainlitUrl = useMemo(() => getAveChainlitUrl(), []);
  const chainlitOrigin = useMemo(() => getOrigin(chainlitUrl), [chainlitUrl]);
  const chainlitIframeUrl = useMemo(
    () => (context ? buildAveChainlitIframeUrl(chainlitUrl, context) : chainlitUrl),
    [chainlitUrl, context],
  );

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const contextId = params.get('ctx');
      const savedContext = consumeAveChainlitContext(contextId);
      const queryContext = createAveChainlitContextFromQuery(params);
      const resolvedContext = savedContext || queryContext;

      if (!resolvedContext) {
        setError('No se encontro la sesion para abrir el Asistente AVE.');
        return;
      }

      setContext(resolvedContext);
      document.title = resolvedContext.assistantName || 'Asistente AVE';
    } catch (err) {
      setError(err.message || 'No se pudo abrir el Asistente AVE.');
    }
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (chainlitOrigin !== '*' && event.origin !== chainlitOrigin) {
        return;
      }

      const payload = parseWindowMessage(event.data);
      if (!payload || !payload.type) {
        return;
      }

      if (
        payload.type === 'utn-ave-chainlit-ready'
        && (!context?.contextId || payload.contextId === context.contextId)
      ) {
        setReady(true);
        setError('');
      }

      if (payload.type === 'utn-ave-chainlit-error') {
        setError(payload.detail || 'Chainlit no pudo iniciar el chat.');
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [chainlitOrigin, context]);

  useEffect(() => {
    if (!context || !iframeLoaded || ready || error) {
      return undefined;
    }

    let attempts = 0;
    const sendContext = () => {
      attempts += 1;
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify(context),
        chainlitOrigin,
      );

      if (attempts >= 30) {
        setError('No se pudo inicializar Chainlit para el Asistente AVE.');
      }
    };

    sendContext();
    const intervalId = window.setInterval(sendContext, 500);
    return () => window.clearInterval(intervalId);
  }, [chainlitOrigin, context, error, iframeLoaded, ready]);

  return (
    <main className="ave-chainlit-page">
      <header className="ave-chainlit-header">
        <div>
          <p>Asistente AVE</p>
          <h1>{context?.assistantName || 'Asistente AVE'}</h1>
        </div>
        <button type="button" onClick={() => window.close()}>
          Cerrar
        </button>
      </header>

      <section className="ave-chainlit-shell">
        {context && (
          <iframe
            ref={iframeRef}
            title={context?.assistantName || 'Asistente AVE'}
            src={chainlitIframeUrl}
            onLoad={() => setIframeLoaded(true)}
            allow="clipboard-write; microphone"
          />
        )}

        {(!ready || error) && (
          <div className="ave-chainlit-status" role={error ? 'alert' : 'status'}>
            <strong>{error ? 'No se pudo abrir el chat' : 'Inicializando chat'}</strong>
            {error && <span>{error}</span>}
          </div>
        )}
      </section>
    </main>
  );
}

export default AveChainlitChat;
